import { useCallback, useRef } from 'react';
import { accountSchema, type DurationUnit } from '@forecastify/shared';
import { useDuration } from './duration';
import { useSession } from './session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · the user menu's *Embernapban* switch, saved optimistically.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` DU-1, `state-rules.md` §7: the whole app flips at once and the save follows; a
 * failed save flips it back. ⚠️ **It reads the row first** because `PUT /account/preferences`
 * carries the version and the rest of the set — the chrome holds neither, and guessing them would
 * reset somebody's timezone from the user menu.
 */
export function useSaveDurationUnit(): (next: DurationUnit) => void {
  const session = useSession();
  const { unit, setUnit } = useDuration();
  /* The session object is new every render — `preferences-boot.tsx` says why it goes in a ref. */
  const sessionRef = useRef(session);
  sessionRef.current = session;

  return useCallback(
    (next: DurationUnit) => {
      if (next === unit) return;
      const previous = unit;
      setUnit(next);

      void (async () => {
        const read = await sessionRef.current.request('/account').catch(() => null);
        const parsed =
          read?.ok === true
            ? accountSchema.safeParse(await read.json().catch(() => null))
            : undefined;
        if (parsed?.success !== true) {
          setUnit(previous);
          return;
        }
        const stored = parsed.data.preferences;
        const saved = await sessionRef.current
          .request('/account/preferences', {
            method: 'PUT',
            body: JSON.stringify({
              locale: stored.locale,
              timezone: stored.timezone,
              dateFormat: stored.dateFormat,
              weekStart: stored.weekStart,
              theme: stored.theme,
              durationUnit: next,
              version: stored.version,
            }),
          })
          .catch(() => null);
        if (saved?.ok !== true) setUnit(previous);
      })();
    },
    [unit, setUnit],
  );
}
